import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, message, Modal, Form, Select, Input, Tag, Popconfirm} from 'antd';
import { TrInputNumber as InputNumber } from './TrInputNumber';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { 
  fetchProductChannelListings, 
  createProductChannelListing, 
  updateProductChannelListing, 
  deleteProductChannelListing,
  updateChannelStock,
  fetchChannels,
  type ProductChannelListingDto,
  type ChannelDto 
} from '../services/channelService';
import { fetchProductVariants, type ProductVariantDto } from '../services/productService';
import { useCanDelete } from '../hooks/useCanDelete';

interface ProductChannelManagerProps {
  productId?: number;
  hasVariants?: boolean;
  isPriceVariant?: boolean;
}

const LISTING_STATUS_OPTIONS = [
  { value: 1, label: 'Taslak', color: 'default' },
  { value: 2, label: 'Aktif', color: 'green' },
  { value: 3, label: 'Pasif', color: 'orange' },
  { value: 4, label: 'Askıya Alındı', color: 'red' },
];

const ProductChannelManager: React.FC<ProductChannelManagerProps> = ({
  productId,
  hasVariants = false, 
  isPriceVariant = false 
}) => {
  const canDelete = useCanDelete();
  const [channels, setChannels] = useState<ChannelDto[]>([]);
  const [variants, setVariants] = useState<ProductVariantDto[]>([]);
  const [productChannelListings, setProductChannelListings] = useState<ProductChannelListingDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [stockModalVisible, setStockModalVisible] = useState(false);
  const [editingListing, setEditingListing] = useState<ProductChannelListingDto | null>(null);
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(null);
  const [form] = Form.useForm();
  const [stockForm] = Form.useForm();

  useEffect(() => {
    if (productId) {
      loadChannels();
      if (hasVariants && isPriceVariant) {
        loadVariants();
      } else {
        // Varyant yoksa direkt listings'i yükle
        loadListings();
      }
    }
  }, [productId, hasVariants, isPriceVariant]);

  const loadChannels = async () => {
    try {
      const data = await fetchChannels();
      setChannels(data.filter(c => c.isActive));
    } catch (error) {
      console.error('Kanallar yüklenirken hata:', error);
    }
  };

  const loadVariants = async () => {
    if (!productId) return;
    try {
      const data = await fetchProductVariants(productId);
      setVariants(data);
    } catch (error) {
      console.error('Varyantlar yüklenirken hata:', error);
    }
  };

  const loadListings = async () => {
    if (!productId) return;
    try {
      setLoading(true);
      // Varyant varsa varyant ID'lerine göre, yoksa product ID'ye göre çek
      if (hasVariants && isPriceVariant && variants.length > 0) {
        // Her varyant için kanalları çek
        const allListings: ProductChannelListingDto[] = [];
        for (const variant of variants) {
          if (variant.id) {
            const variantListings = await fetchProductChannelListings(undefined, variant.id);
            allListings.push(...variantListings);
          }
        }
        setProductChannelListings(allListings);
      } else if (!hasVariants || !isPriceVariant) {
        // Ürün için kanalları çek
        const listings = await fetchProductChannelListings(productId);
        setProductChannelListings(listings);
      }
    } catch (error) {
      message.error('Kanal listeleri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Variants yüklendiğinde listings'i yeniden yükle
  useEffect(() => {
    if (productId) {
      if (hasVariants && isPriceVariant) {
        // Variant'lar yüklendikten sonra listings'i yükle
        if (variants.length > 0) {
          loadListings();
        }
      } else {
        // Varyant yoksa direkt yükle
        loadListings();
      }
    }
  }, [variants, productId, hasVariants, isPriceVariant]);

  const handleCreate = (variantId?: number) => {
    setEditingListing(null);
    setSelectedVariantId(variantId || null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (listing: ProductChannelListingDto) => {
    setEditingListing(listing);
    setSelectedVariantId(listing.productVariantId || null);
    form.setFieldsValue({
      channelId: listing.channelId,
      listingStatus: listing.listingStatus,
      channelSku: listing.channelSku});
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteProductChannelListing(id);
      message.success('Kanal başarıyla kaldırıldı');
      loadListings();
    } catch (error) {
      message.error('Kanal kaldırılırken hata oluştu');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingListing) {
        await updateProductChannelListing(editingListing.id, {
          listingStatus: values.listingStatus,
          channelSku: values.channelSku});
        message.success('Kanal başarıyla güncellendi');
      } else {
        if (!productId) {
          message.error('Ürün ID bulunamadı');
          return;
        }

        await createProductChannelListing({
          productId: hasVariants && isPriceVariant && selectedVariantId ? undefined : productId,
          productVariantId: hasVariants && isPriceVariant && selectedVariantId ? selectedVariantId : undefined,
          channelId: values.channelId,
          listingStatus: values.listingStatus,
          channelSku: values.channelSku});
        message.success('Kanal başarıyla eklendi');
      }
      setModalVisible(false);
      loadListings();
    } catch (error: any) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('İşlem sırasında hata oluştu');
      }
    }
  };

  const handleStockEdit = (listing: ProductChannelListingDto) => {
    setEditingListing(listing);
    stockForm.setFieldsValue({
      quantity: listing.quantity || 0,
      reservedQuantity: listing.reservedQuantity || 0});
    setStockModalVisible(true);
  };

  const handleStockSubmit = async () => {
    if (!editingListing) return;
    
    try {
      const values = await stockForm.validateFields();
      await updateChannelStock(editingListing.id, {
        quantity: values.quantity,
        reservedQuantity: values.reservedQuantity});
      message.success('Stok başarıyla güncellendi');
      setStockModalVisible(false);
      loadListings();
    } catch (error) {
      message.error('Stok güncellenirken hata oluştu');
    }
  };

  const getStatusTag = (status: number) => {
    const option = LISTING_STATUS_OPTIONS.find(opt => opt.value === status);
    return option ? (
      <Tag color={option.color}>{option.label}</Tag>
    ) : (
      <Tag>{status}</Tag>
    );
  };

  // Varyant varsa, varyant bazlı tablo göster
  if (hasVariants && isPriceVariant && variants.length > 0) {
    const variantColumns = [
      {
        title: 'SKU',
        dataIndex: 'sku',
        key: 'sku',
        width: 150},
      {
        title: 'Değerler',
        key: 'values',
        render: (_: any, record: ProductVariantDto) => 
          record.values?.map(v => `${v.variantName}: ${v.variantValue}`).join(', ') || '-'},
      {
        title: 'Kanallar',
        key: 'channels',
        render: (_: any, record: ProductVariantDto) => {
          const variantListings = productChannelListings.filter(
            l => l.productVariantId === record.id
          );
          return (
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              {variantListings.length > 0 ? (
                variantListings.map(listing => (
                  <div key={listing.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Tag>{listing.channelName}</Tag>
                    {getStatusTag(listing.listingStatus)}
                    <Button
                      type="link"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => handleEdit(listing)}
                    />
                    {canDelete && (
                    <Popconfirm
                      title="Bu kanalı kaldırmak istediğinize emin misiniz?"
                      onConfirm={() => handleDelete(listing.id)}
                      okText="Evet"
                      cancelText="Hayır"
                    >
                      <Button
                        type="link"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                      />
                    </Popconfirm>
                    )}
                  </div>
                ))
              ) : (
                <span style={{ color: '#999' }}>Kanal atanmamış</span>
              )}
              <Button
                type="link"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => handleCreate(record.id)}
              >
                Kanal Ekle
              </Button>
            </Space>
          );
        }},
      {
        title: 'Stok',
        key: 'stock',
        render: (_: any, record: ProductVariantDto) => {
          const variantListings = productChannelListings.filter(
            l => l.productVariantId === record.id
          );
          return (
            <Space direction="vertical" size="small">
              {variantListings.map(listing => (
                <div key={listing.id}>
                  <Tag>{listing.channelName}:</Tag>
                  <span>Mevcut: {listing.availableQuantity || 0}</span>
                  {listing.quantity !== undefined && (
                    <Button
                      type="link"
                      size="small"
                      onClick={() => handleStockEdit(listing)}
                    >
                      Düzenle
                    </Button>
                  )}
                </div>
              ))}
            </Space>
          );
        }},
    ];

    return (
      <Card title="Kanal Yönetimi" size="small">
        <Table
          columns={variantColumns}
          dataSource={variants}
          rowKey="id"
          loading={loading}
          pagination={false}
          size="small"
        />

        <Modal
          title={editingListing ? 'Kanal Düzenle' : 'Yeni Kanal Ekle'}
          open={modalVisible}
          onOk={handleSubmit}
          onCancel={() => setModalVisible(false)}
          okText="Kaydet"
          cancelText="İptal"
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="channelId"
              label="Kanal"
              rules={[{ required: true, message: 'Kanal seçiniz' }]}
            >
              <Select 
                placeholder="Kanal seçiniz"
                disabled={!!editingListing}
              >
                {channels.map(channel => (
                  <Select.Option key={channel.id} value={channel.id}>
                    {channel.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="listingStatus"
              label="Durum"
              rules={[{ required: true, message: 'Durum seçiniz' }]}
              initialValue={1}
            >
              <Select placeholder="Durum seçiniz">
                {LISTING_STATUS_OPTIONS.map(opt => (
                  <Select.Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="channelSku"
              label="Kanal SKU"
            >
              <Input placeholder="Kanal SKU'su (opsiyonel)" />
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title="Kanal Stok Düzenle"
          open={stockModalVisible}
          onOk={handleStockSubmit}
          onCancel={() => setStockModalVisible(false)}
          okText="Kaydet"
          cancelText="İptal"
        >
          {editingListing && (
            <Form form={stockForm} layout="vertical">
              <Form.Item label="Kanal">
                <span>{editingListing.channelName}</span>
              </Form.Item>
              <Form.Item
                name="quantity"
                label="Miktar"
                rules={[{ required: true, message: 'Miktar giriniz' }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                name="reservedQuantity"
                label="Rezerve Miktar"
                rules={[{ required: true, message: 'Rezerve miktar giriniz' }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Form>
          )}
        </Modal>
      </Card>
    );
  }

  // Varyant yoksa, ürün bazlı tablo göster
  const productColumns = [
    {
      title: 'Kanal',
      dataIndex: 'channelName',
      key: 'channelName'},
    {
      title: 'Durum',
      dataIndex: 'listingStatus',
      key: 'listingStatus',
      render: (status: number) => getStatusTag(status)},
    {
      title: 'Kanal SKU',
      dataIndex: 'channelSku',
      key: 'channelSku',
      render: (sku: string) => sku || '-'},
    {
      title: 'Stok',
      key: 'stock',
      render: (_: any, record: ProductChannelListingDto) => (
        <Space>
          <span>Mevcut: {record.availableQuantity || 0}</span>
          <Button
            type="link"
            size="small"
            onClick={() => handleStockEdit(record)}
          >
            Düzenle
          </Button>
        </Space>
      )},
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: ProductChannelListingDto) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Düzenle
          </Button>
          {canDelete && (
          <Popconfirm
            title="Bu kanalı kaldırmak istediğinize emin misiniz?"
            onConfirm={() => handleDelete(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Sil
            </Button>
          </Popconfirm>
          )}
        </Space>
      )},
  ];

  return (
    <Card 
      title="Kanal Yönetimi" 
      size="small"
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => handleCreate()}
        >
          Kanal Ekle
        </Button>
      }
    >
      <Table
        columns={productColumns}
        dataSource={productChannelListings}
        rowKey="id"
        loading={loading}
        pagination={false}
        locale={{ emptyText: 'Henüz kanal atanmamış' }}
      />

      <Modal
        title={editingListing ? 'Kanal Düzenle' : 'Yeni Kanal Ekle'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        okText="Kaydet"
        cancelText="İptal"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="channelId"
            label="Kanal"
            rules={[{ required: true, message: 'Kanal seçiniz' }]}
          >
            <Select 
              placeholder="Kanal seçiniz"
              disabled={!!editingListing}
            >
              {channels.map(channel => (
                <Select.Option key={channel.id} value={channel.id}>
                  {channel.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="listingStatus"
            label="Durum"
            rules={[{ required: true, message: 'Durum seçiniz' }]}
            initialValue={1}
          >
            <Select placeholder="Durum seçiniz">
              {LISTING_STATUS_OPTIONS.map(opt => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="channelSku"
            label="Kanal SKU"
          >
            <Input placeholder="Kanal SKU'su (opsiyonel)" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Kanal Stok Düzenle"
        open={stockModalVisible}
        onOk={handleStockSubmit}
        onCancel={() => setStockModalVisible(false)}
        okText="Kaydet"
        cancelText="İptal"
      >
        {editingListing && (
          <Form form={stockForm} layout="vertical">
            <Form.Item label="Kanal">
              <span>{editingListing.channelName}</span>
            </Form.Item>
            <Form.Item
              name="quantity"
              label="Miktar"
              rules={[{ required: true, message: 'Miktar giriniz' }]}
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="reservedQuantity"
              label="Rezerve Miktar"
              rules={[{ required: true, message: 'Rezerve miktar giriniz' }]}
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </Card>
  );
};

export default ProductChannelManager;

